import { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { shortCurrency } from 'dash-common';
import {
  Checkbox,
  Flex,
  FormContext,
  Text,
  mortgage,
  useScreenResolution,
} from 'dash-ui';

import { FormFields } from 'pages/Dashboard/components/MortgageCalculatorWidget/components/MortgageForm/constants/constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    y: {
      ticks: {
        callback: shortCurrency,
      },
    },
  },
};

const reduceForYears = (
  obj: {
    labels: string[];
    data: number[];
    totalInterest: number;
    totalPayment: number;
  },
  item: {
    balance: number;
    count: number;
    totalInterest: number;
    totalPayment: number;
  },
  index: number
) => {
  if ((index + 1) % 12 !== 0) return obj;
  obj.labels.push((new Date().getFullYear() + item.count / 12 - 1).toString());
  obj.data.push(item.balance);
  obj.totalInterest = item.totalInterest;
  obj.totalPayment = item.totalPayment;
  return obj;
};

const reduceForMonths = (
  obj: {
    labels: string[];
    data: number[];
    totalInterest: number;
    totalPayment: number;
  },
  item: {
    balance: number;
    count: number;
    totalInterest: number;
    totalPayment: number;
  }
) => {
  obj.labels.push(
    moment()
      .set('month', moment().get('month') + item.count)
      .format('MMM yyyy')
  );
  obj.data.push(item.balance);
  obj.totalInterest = item.totalInterest;
  obj.totalPayment = item.totalPayment;
  return obj;
};

const HeaderStyle = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
  @media ${({ theme }) => theme.devices.mobile} {
    flex-direction: column;
    justify-content: flex-start;
  }
`;

const ChartContainer = styled(Flex)`
  @media ${({ theme }) => theme.devices.mobile} {
    overflow-x: scroll;
  }
`;

const MortgageChart = () => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const { formState, stateHandlers } = useContext(FormContext);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const {
    totalPrice,
    downPayment,
    interestRate,
    term,
    taxRate = 0,
    insuranceRate = 0,
    lastRecalculationMoment,
  } = formState;

  const { paymentSchedule, loanAmount } = useMemo(
    () =>
      mortgage.calculateMortgage({
        totalPrice,
        downPayment,
        interestRate: interestRate / 100,
        months: term * 12,
        taxRate,
        insuranceRate,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lastRecalculationMoment]
  );

  const { labels, data, totalInterest, totalPayment } = useMemo(() => {
    return paymentSchedule.reduce(term > 5 ? reduceForYears : reduceForMonths, {
      labels: [],
      data: [],
      totalInterest: 0,
      totalPayment: 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentSchedule]);

  const handleCheckboxChange = (key: string) => (checked: boolean) => {
    stateHandlers[key](checked);
  };

  const getChartData = useCallback(
    (rate: number) => {
      const rates =
        rate > 0
          ? Array.from({ length: term }, (_, index) => (index + 1) * rate)
          : [];

      return {
        data: rates,
        total: rates.at(-1) || 0,
      };
    },
    [term]
  );

  const { data: taxData, total: taxTotal } = getChartData(taxRate);
  const { data: insuranceData, total: insuranceTotal } =
    getChartData(insuranceRate);

  return (
    <Flex
      w="100%"
      pl={[0, 8, 8, 8]}
      pb={[0, 8, 8, 8]}
      flex={1}
      flexDirection="column"
    >
      <HeaderStyle mt={[4, 4, 10.5, 10.5]} w="100%" flex={1}>
        <Flex flex={1}>
          <Text sm color={theme.colors.n80}>
            {t('pages:dashboardPage.mortgageWidget.totalPayments')}{' '}
            <Text semibold sm as="span" bold color={theme.colors.n90}>
              $
              {mortgage
                .nextPenny(totalInterest + loanAmount)
                .toLocaleString('en')}
            </Text>
          </Text>
        </Flex>
        <Flex mt={[4, 0, 0, 0]} flex={1} color={theme.colors.n80}>
          <Text sm>
            {t('pages:dashboardPage.mortgageWidget.monthlyPayments')}{' '}
            <Text semibold sm as="span" bold color={theme.colors.n90}>
              ${mortgage.nextPenny(totalPayment).toLocaleString('en')}
            </Text>
          </Text>
        </Flex>
        <Flex mt={[4, 0, 0, 0]} flex={1} color={theme.colors.n80}>
          <Text sm>
            {t('pages:dashboardPage.mortgageWidget.totalInterestPaid')}{' '}
            <Text semibold sm as="span" bold color={theme.colors.n90}>
              ${mortgage.nextPenny(totalInterest).toLocaleString('en')}
            </Text>
          </Text>
        </Flex>
      </HeaderStyle>
      <Flex w="100%" mt={4} flexDirection={isMobile ? 'column' : 'row'}>
        <Flex flex={1}>
          <Text sm>
            {t('pages:dashboardPage.mortgageWidget.totalPropertyTax')}{' '}
            <Text semibold sm as="span" bold color={theme.colors.n90}>
              ${mortgage.nextPenny(taxTotal).toLocaleString('en')}
            </Text>
          </Text>
        </Flex>
        <Flex flex={1} mt={[4, 0, 0, 0]}>
          <Text sm>
            {t('pages:dashboardPage.mortgageWidget.totalPropertyInsurance')}{' '}
            <Text semibold sm as="span" bold color={theme.colors.n90}>
              ${mortgage.nextPenny(insuranceTotal).toLocaleString('en')}
            </Text>
          </Text>
        </Flex>
        <Flex flex={1} />
      </Flex>
      <ChartContainer w="100%" mt={[4, 4, 8, 8]}>
        <Flex w="100%" minWidth={[600, 'unset', 'unset', 'unset']}>
          <Bar
            // @ts-ignore
            options={options}
            data={{
              labels,
              datasets: [
                {
                  label:
                    t('pages:dashboardPage.mortgageWidget.mortgageBalance') ||
                    '',
                  data: data,
                  backgroundColor: theme.colors.g20,
                },
                ...(formState.taxChecked && taxData.length > 0
                  ? [
                      {
                        label: t(
                          'pages:dashboardPage.mortgageWidget.propertyTax'
                        ),
                        data: taxData,
                        backgroundColor: theme.colors.r20,
                      },
                    ]
                  : []),
                ...(formState.insuranceChecked && insuranceData.length > 0
                  ? [
                      {
                        label: t(
                          'pages:dashboardPage.mortgageWidget.propertyInsurance'
                        ),
                        data: insuranceData,
                        backgroundColor: theme.colors.y20,
                      },
                    ]
                  : []),
              ],
            }}
          />
        </Flex>
      </ChartContainer>
      <Flex mt={4} ml={[0, 0, 10, 10]}>
        <Checkbox
          label={
            t('pages:dashboardPage.mortgageWidget.propertyInsurance') || ''
          }
          mr={[4, 6, 6, 6]}
          checked={formState.insuranceChecked}
          onChange={handleCheckboxChange(FormFields.InsuranceChecked)}
        />
        <Checkbox
          label={t('pages:dashboardPage.mortgageWidget.propertyTax') || ''}
          checked={formState.taxChecked}
          onChange={handleCheckboxChange(FormFields.TaxChecked)}
        />
      </Flex>
    </Flex>
  );
};

export default MortgageChart;
